import { useState, useMemo, useEffect } from 'react';
import { Title } from 'react-admin';
import { SetupGuide } from './SetupGuide';
import {
  Box, Typography, Card, CardContent, Grid2 as Grid,
  Table, TableBody, TableCell, TableHead, TableRow,
  Skeleton, Chip, IconButton, Tooltip, Breadcrumbs, Link,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip as ChartTooltip, Legend,
} from 'recharts';
import { isOrgPathWithin, useRollup, useTrend } from '../hooks/useRollup';
import { DEMO_ROLLUP_ROOT } from '../fixtures/analytics-fixture';

const PERIODS = ['2025-12', '2026-01', '2026-02', '2026-03', '2026-04', '2026-05', '2026-06'];
const CURRENT_PERIOD = '2026-06';

const passColor = (avg: number) =>
  avg >= 0.85 ? '#388e3c' : avg >= 0.70 ? '#f57c00' : '#d32f2f';

const pct = (v: number) => `${Math.round(v * 100)}%`;

function MetricCard({ label, value, sub, loading }: {
  label: string; value: string; sub?: string; loading: boolean;
}) {
  return (
    <Card>
      <CardContent sx={{ textAlign: 'center', py: 2 }}>
        {loading
          ? <Skeleton variant="text" width={80} height={48} sx={{ mx: 'auto' }} />
          : <Typography variant="h4" fontWeight={700} color="primary">{value}</Typography>}
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        {sub && <Typography variant="caption" color="text.disabled">{sub}</Typography>}
      </CardContent>
    </Card>
  );
}

function PassRateHeatmap({ children, loading }: {
  children: typeof DEMO_ROLLUP_ROOT.children; loading: boolean;
}) {
  return (
    <Card>
      <CardContent>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Pass Rate by Echelon
        </Typography>
        {loading
          ? <Skeleton variant="rectangular" height={80} />
          : (
            <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
              {(children ?? []).map((c) => (
                <Tooltip key={c.orgPath} title={`${c.orgPath} — ${pct(c.total.avg)} (${c.total.count} attempts)`}>
                  <Box
                    sx={{
                      width: 100, height: 60, borderRadius: 1, display: 'flex',
                      flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                      bgcolor: passColor(c.total.avg), color: '#fff', cursor: 'default',
                    }}
                  >
                    <Typography variant="caption" sx={{ fontSize: 10, opacity: 0.85 }}>
                      {c.orgName ?? c.orgPath.split('/').pop()}
                    </Typography>
                    <Typography variant="body1" fontWeight={700}>{pct(c.total.avg)}</Typography>
                  </Box>
                </Tooltip>
              ))}
            </Box>
          )}
      </CardContent>
    </Card>
  );
}

export function CommandDashboard({ rootOrgPath }: { rootOrgPath: string }) {
  const [drillPath, setDrillPath] = useState(rootOrgPath);
  const activeDrillPath = isOrgPathWithin(rootOrgPath, drillPath) ? drillPath : rootOrgPath;
  const { data, loading } = useRollup(activeDrillPath, 'pass_rate', CURRENT_PERIOD, 'children');
  const { trend, loading: trendLoading } = useTrend(activeDrillPath, 'pass_rate', PERIODS);

  useEffect(() => {
    setDrillPath(rootOrgPath);
  }, [rootOrgPath]);

  const children = useMemo(
    () => (data?.children ?? []).filter((child) =>
      child.orgPath !== activeDrillPath &&
      isOrgPathWithin(activeDrillPath, child.orgPath) &&
      isOrgPathWithin(rootOrgPath, child.orgPath)),
    [activeDrillPath, data?.children, rootOrgPath],
  );

  const breadcrumbs = useMemo(() => {
    const rootParts = rootOrgPath.split('/').filter(Boolean);
    const relativeParts = activeDrillPath.slice(rootOrgPath.length).split('/').filter(Boolean);
    return relativeParts.reduce<Array<{ label: string; path: string }>>(
      (acc, part) => {
        const prev = acc[acc.length - 1]?.path ?? rootOrgPath;
        const path = prev === '/' ? `/${part}` : `${prev}/${part}`;
        acc.push({ label: part.toUpperCase(), path });
        return acc;
      },
      [{ label: rootParts.at(-1)?.toUpperCase() ?? 'ROOT', path: rootOrgPath }],
    );
  }, [activeDrillPath, rootOrgPath]);

  const canDrillDown = children.length > 0;

  return (
    <Box p={3}>
      <SetupGuide />
      <Title title="Command Dashboard" />

      {/* Breadcrumb nav */}
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        {activeDrillPath !== rootOrgPath && (
          <IconButton size="small" onClick={() => {
            const parts = activeDrillPath.split('/').filter(Boolean);
            parts.pop();
            const parent = parts.length ? `/${parts.join('/')}` : rootOrgPath;
            setDrillPath(isOrgPathWithin(rootOrgPath, parent) ? parent : rootOrgPath);
          }}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        )}
        <Breadcrumbs>
          {breadcrumbs.map((b, i) => (
            i === breadcrumbs.length - 1
              ? <Typography key={b.path} fontWeight={600}>{b.label}</Typography>
              : (
                <Link
                  key={b.path}
                  component="button"
                  underline="hover"
                  onClick={() => {
                    if (isOrgPathWithin(rootOrgPath, b.path)) setDrillPath(b.path);
                  }}
                >
                  {b.label}
                </Link>
              )
          ))}
        </Breadcrumbs>
      </Box>

      {/* KPI cards */}
      <Grid container spacing={2} mb={3}>
        <Grid size={{ xs: 6, sm: 3 }}>
          <MetricCard
            label="Total Attempts"
            value={loading ? '—' : (data?.total.count ?? 0).toLocaleString()}
            sub={CURRENT_PERIOD}
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <MetricCard
            label="Pass Rate"
            value={loading ? '—' : pct(data?.total.avg ?? 0)}
            sub="this period"
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <MetricCard
            label="Passing Members"
            value={loading ? '—' : (data?.total.sum ?? 0).toLocaleString()}
            sub="unique passes"
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <MetricCard
            label="Child Echelons"
            value={loading ? '—' : String(children.length)}
            sub="reporting units"
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Trend + heatmap */}
      <Grid container spacing={2} mb={3}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Pass Rate Trend (6 months)
              </Typography>
              {trendLoading
                ? <Skeleton variant="rectangular" height={220} />
                : (
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={trend} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                      <YAxis
                        tickFormatter={(v) => `${Math.round(v * 100)}%`}
                        domain={[0.5, 1.0]}
                        tick={{ fontSize: 12 }}
                      />
                      <ChartTooltip formatter={(v) => typeof v === 'number' ? pct(v) : v} />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="value"
                        name="Pass Rate"
                        stroke="#1565c0"
                        strokeWidth={2}
                        dot={{ r: 4 }}
                        activeDot={{ r: 6 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                )}
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <PassRateHeatmap children={children} loading={loading} />
        </Grid>
      </Grid>

      {/* Child echelon comparison table */}
      {canDrillDown && (
        <Card>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Echelon Comparison — click to drill down
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Unit</TableCell>
                  <TableCell align="right">Attempts</TableCell>
                  <TableCell align="right">Pass Rate</TableCell>
                  <TableCell align="right">Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {children.map((c) => (
                  <TableRow
                    key={c.orgPath}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setDrillPath(c.orgPath)}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {c.orgName ?? c.orgPath.split('/').pop()?.toUpperCase()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {c.orgPath}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{c.total.count.toLocaleString()}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {pct(c.total.avg)}
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        label={c.total.avg >= 0.85 ? 'On Track' : c.total.avg >= 0.70 ? 'Monitor' : 'At Risk'}
                        size="small"
                        sx={{
                          bgcolor: passColor(c.total.avg),
                          color: '#fff',
                          fontWeight: 600,
                          fontSize: 11,
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
